import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../context/auth';
import topApi from '../../components/api/ToPApi';
import Message from '../../components/ui/Message';
import { errorMessage } from '../../utils/access';
import { CatalogFeature, featureIcon, formatDay, formatMoney, formatTotals, MerchantFeature, MonthlyTotal, totalOf } from './featureUi';

interface MyFeatures {
    merchant: { id: number; name: string; countryId: number };
    features: MerchantFeature[];
    monthlyTotal: MonthlyTotal[];
    catalog: CatalogFeature[];
    baseFeatures: string[];
    serviceActiveUntil: string | null;   // todo el servicio cancelado: hasta esta fecha; después el comercio queda inactivo
    canManage: boolean;
}

/**
 * Funcionalidades del comercio en sesión. Su administrador puede agregar las que no tiene y cancelar las que
 * tiene, o todo el servicio (incluida la base). Funciona como suscripción: al cancelar sigue usando lo cancelado
 * hasta el fin del mes ya pagado; si canceló todo el servicio, después el comercio queda inactivo.
 */
export const MyFeaturesPage = () => {
    const [data, setData] = useState<MyFeatures | null>(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<string[]>([]);
    const [confirming, setConfirming] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [pendingCancel, setPendingCancel] = useState<MerchantFeature | null>(null);
    const [confirmCancelAll, setConfirmCancelAll] = useState(false);
    const { reloadResources } = useContext(AuthContext);

    useEffect(() => {
        topApi.get<MyFeatures>('/features/mine')
            .then(resp => setData(resp.data))
            .catch(e => setError(errorMessage(e)))
            .finally(() => setLoading(false));
    }, []);

    const available = useMemo(() => (data?.catalog ?? []).filter(f => !f.active), [data]);
    const toAdd = available.filter(f => selected.includes(f.code));
    const addTotal = formatTotals(totalOf(toAdd));
    // Lo que se cobra desde el próximo mes: sin las canceladas
    const renewing = (data?.features ?? []).filter(f => !f.activeUntil);
    const newTotal = formatTotals(totalOf([...renewing, ...toAdd]));
    const hasCancelled = renewing.length < (data?.features.length ?? 0);

    const toggle = (code: string) =>
        setSelected(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);

    const add = async () => {
        setConfirming(false);
        setSaving(true);
        try {
            const resp = await topApi.post<MyFeatures & { added: MerchantFeature[] }>('/features/mine', { features: selected });
            setData(resp.data);
            setSelected([]);
            setSuccess(`Listo: se activó ${resp.data.added.map(f => f.name).join(', ')}. Las opciones nuevas ya están en el menú.`);
            reloadResources().catch(() => undefined);
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    const cancel = async () => {
        const feature = pendingCancel;
        setPendingCancel(null);
        if (!feature) return;
        setSaving(true);
        try {
            const resp = await topApi.post<MyFeatures & { cancelled: { name: string; activeUntil: string } }>('/features/mine/cancel', { feature: feature.code });
            setData(resp.data);
            setSuccess(`Se canceló ${resp.data.cancelled.name}. La puede seguir usando hasta el ${formatDay(resp.data.cancelled.activeUntil)} y no se cobrará el próximo mes.`);
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    const resume = async (feature: MerchantFeature) => {
        setSaving(true);
        try {
            const resp = await topApi.post<MyFeatures>('/features/mine/resume', { feature: feature.code });
            setData(resp.data);
            setSuccess(`Listo: ${feature.name} se mantiene y se seguirá cobrando cada mes.`);
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    const cancelAll = async (reason?: string) => {
        setConfirmCancelAll(false);
        setSaving(true);
        try {
            const resp = await topApi.post<MyFeatures & { serviceCancelled: { activeUntil: string } }>('/features/mine/cancel-all', { reason: reason || undefined });
            setData(resp.data);
            setSuccess(`Se canceló el servicio. Puede seguir usando el sistema hasta el ${formatDay(resp.data.serviceCancelled.activeUntil)}; después el comercio quedará inactivo y no se cobrará el próximo mes.`);
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    const resumeAll = async () => {
        setSaving(true);
        try {
            const resp = await topApi.post<MyFeatures>('/features/mine/resume-all');
            setData(resp.data);
            setSuccess('Listo: su servicio se mantiene y se seguirá cobrando cada mes.');
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    const isBase = (code: string) => (data?.baseFeatures ?? ['retail']).includes(code);
    const serviceCancelled = !!data?.serviceActiveUntil;

    return (
        <div className="mt-5 container-fluid px-4" style={{ flex: 1, marginTop: 10 }}>
            <div className="mb-4">
                <h3 className="mb-1">Mis funcionalidades</h3>
                <p className="text-muted mb-0">Lo que tiene contratado su comercio y lo que puede agregar.</p>
            </div>

            {data?.serviceActiveUntil && (
                <div className="alert alert-warning d-flex flex-wrap align-items-center gap-2" role="alert">
                    <i className="bi bi-exclamation-triangle fs-5" />
                    <div className="flex-grow-1">
                        <strong>Su servicio está cancelado.</strong> Puede seguir usando el sistema hasta el {formatDay(data.serviceActiveUntil)};
                        después el comercio quedará inactivo.
                    </div>
                    {data.canManage && (
                        <button type="button" className="btn btn-sm btn-warning" disabled={saving} onClick={resumeAll}>
                            <i className="bi bi-arrow-counterclockwise me-1" />Mantener el servicio
                        </button>
                    )}
                </div>
            )}

            {loading ? (
                <div className="text-center py-5"><span className="spinner-border text-primary" /></div>
            ) : data && (
                <div className="row g-4">
                    {/* ACTIVAS */}
                    <div className="col-lg-5">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-body">
                                <h6 className="text-muted text-uppercase small mb-3">Activas</h6>
                                <ul className="list-group list-group-flush">
                                    {data.features.map(f => (
                                        <li key={f.code} className="list-group-item px-0">
                                            <div className="d-flex align-items-center gap-3">
                                                <span className={`fs-5 ${f.activeUntil ? 'text-muted' : 'text-primary'}`}><i className={`bi ${featureIcon(f.code)}`} /></span>
                                                <div className="flex-grow-1">
                                                    <div className="fw-medium">{f.name}</div>
                                                    {f.activeUntil
                                                        ? <div className="small text-warning-emphasis"><i className="bi bi-hourglass-split me-1" />Cancelada: la puede usar hasta el {formatDay(f.activeUntil)}</div>
                                                        : f.activatedAt && <div className="small text-muted">Desde {new Date(f.activatedAt).toLocaleDateString('es-CR')}</div>}
                                                </div>
                                                <span className="small" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(f.monthlyPrice, f.currency)}</span>
                                            </div>
                                            {data.canManage && !isBase(f.code) && !serviceCancelled && (
                                                <div className="text-end mt-1">
                                                    {f.activeUntil ? (
                                                        <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" disabled={saving} onClick={() => resume(f)}>
                                                            <i className="bi bi-arrow-counterclockwise me-1" />Mantener {f.name}
                                                        </button>
                                                    ) : (
                                                        <button type="button" className="btn btn-link btn-sm p-0 text-danger text-decoration-none" disabled={saving} onClick={() => setPendingCancel(f)}>
                                                            Cancelar
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                                <div className="d-flex justify-content-between border-top pt-3 mt-2">
                                    <span className="text-muted">Total este mes</span>
                                    <strong>{formatTotals(data.monthlyTotal)}</strong>
                                </div>
                                {hasCancelled && (
                                    <div className="d-flex justify-content-between small mt-1">
                                        <span className="text-muted">Desde el próximo mes</span>
                                        <strong>{renewing.length ? formatTotals(totalOf(renewing)) : 'Sin cobro'}</strong>
                                    </div>
                                )}
                                {data.canManage && !serviceCancelled && (
                                    <div className="border-top pt-3 mt-3">
                                        <button type="button" className="btn btn-outline-danger btn-sm" disabled={saving} onClick={() => setConfirmCancelAll(true)}>
                                            <i className="bi bi-x-octagon me-1" />Cancelar todo el servicio
                                        </button>
                                        <div className="small text-muted mt-1">Incluye Retail e inventario. Al terminar el mes el comercio quedará inactivo.</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* DISPONIBLES */}
                    <div className="col-lg-7">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-body">
                                <h6 className="text-muted text-uppercase small mb-3">Disponibles para agregar</h6>
                                {available.length === 0 ? (
                                    <p className="text-muted mb-0"><i className="bi bi-check2-all me-2" />Ya tiene todas las funcionalidades.</p>
                                ) : (<>
                                    {!data.canManage && (
                                        <div className="alert alert-info small"><i className="bi bi-info-circle me-2" />Solo el administrador del comercio puede agregar funcionalidades.</div>
                                    )}
                                    <div className="d-flex flex-column gap-2">
                                        {available.map(f => {
                                            const on = selected.includes(f.code);
                                            return (
                                                <label key={f.code} className={`d-flex align-items-center gap-3 border rounded-3 p-3 ${on ? 'border-primary bg-primary-subtle' : ''}`}
                                                    style={{ cursor: data.canManage ? 'pointer' : 'default' }}>
                                                    <input type="checkbox" className="form-check-input mt-0" checked={on} disabled={!data.canManage || saving}
                                                        onChange={() => toggle(f.code)} />
                                                    <span className="text-primary fs-5"><i className={`bi ${featureIcon(f.code)}`} /></span>
                                                    <span className="flex-grow-1">
                                                        <span className="fw-medium d-block">{f.name}</span>
                                                        {f.description && <span className="small text-muted">{f.description}</span>}
                                                    </span>
                                                    <span className="text-nowrap fw-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                                        {formatMoney(f.monthlyPrice, f.currency)}<small className="text-muted fw-normal">/mes</small>
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                    {data.canManage && (
                                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                                            <span className="small text-muted">{toAdd.length > 0 ? <>Nuevo total mensual: <strong className="text-body">{newTotal}</strong></> : 'Seleccione lo que desea agregar.'}</span>
                                            <button type="button" className="btn btn-primary" disabled={!toAdd.length || saving} onClick={() => setConfirming(true)}>
                                                {saving ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-plus-lg me-1" />}
                                                Agregar {toAdd.length > 0 ? `(${toAdd.length})` : ''}
                                            </button>
                                        </div>
                                    )}
                                </>)}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {confirming && (
                <Message
                    type="info"
                    title="Agregar funcionalidades"
                    message={`Se activará ${toAdd.map(f => f.name).join(', ')} por ${addTotal} adicionales al mes. Su nuevo total mensual será ${newTotal}.`}
                    okText="Activar"
                    okWidth="100px"
                    showCancel
                    closeDialog={() => setConfirming(false)}
                    handleCancel={() => setConfirming(false)}
                    handleOk={add}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}
            {confirmCancelAll && (
                <Message
                    type="info"
                    title="Cancelar todo el servicio"
                    message="Se cancelarán todas sus funcionalidades, incluida Retail e inventario. Podrá seguir usando el sistema hasta el fin de este mes, que ya está pagado; después el comercio quedará inactivo y no se cobrará el próximo mes. Puede mantener el servicio antes de esa fecha."
                    okText="Cancelar servicio"
                    okWidth="150px"
                    showCancel
                    cancelText="Volver"
                    showReasonField
                    reasonLabel="Motivo (opcional)"
                    reasonRequired={false}
                    closeDialog={() => setConfirmCancelAll(false)}
                    handleCancel={() => setConfirmCancelAll(false)}
                    handleOk={cancelAll}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}
            {pendingCancel && (
                <Message
                    type="info"
                    title={`Cancelar ${pendingCancel.name}`}
                    message={`Podrá seguir usando ${pendingCancel.name} hasta el fin de este mes, que ya está pagado. Desde el próximo mes no se cobrará y dejará de estar disponible. Puede mantenerla antes de esa fecha.`}
                    okText="Cancelar servicio"
                    okWidth="150px"
                    showCancel
                    cancelText="Volver"
                    closeDialog={() => setPendingCancel(null)}
                    handleCancel={() => setPendingCancel(null)}
                    handleOk={cancel}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}
            {error && <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />}
            {success && <Message type="success" title="Funcionalidades activadas" message={success} closeDialog={() => setSuccess('')} handleOk={() => setSuccess('')} backgroundColor="rgba(0,0,0,0.4)" />}
        </div>
    );
};
